import { each, assign } from 'lodash';
import { SnackbarProgrammatic } from 'buefy';
import { BaseUIService } from '/@/modules/core/ui/services/BaseUIService';
import type { App } from 'vue';

let snackbarInstance: SnackbarProgrammatic | null = null;

// Type definitions for Buefy Vue 3
type ColorModifiers = 'is-primary' | 'is-info' | 'is-success' | 'is-warning' | 'is-danger' | 'is-dark' | 'is-light';
type GlobalPositions = 'is-top' | 'is-bottom' | 'is-top-right' | 'is-top-left' | 'is-bottom-right' | 'is-bottom-left';

interface BNoticeComponent {
  close: () => void;
  $refs: Record<string, unknown>;
}

type SnackbarOptions = {
  message?: string;
  type?: ColorModifiers;
  position?: GlobalPositions;
  duration?: number;
  container?: string;
  actionText?: string | null;
  queue?: boolean;
  indefinite?: boolean;
  onAction?: () => void;
};

type SnackbarBannerOptions = SnackbarOptions & {
  icon?: string;
  isCentered?: boolean;
};

export class SnackbarService extends BaseUIService {
  setApp(app: App): void {
    snackbarInstance = new SnackbarProgrammatic(app);
  }

  show(message: string, options: Partial<SnackbarOptions> = {}): BNoticeComponent {
    if (!snackbarInstance) {
      throw new Error('SnackbarService: app not initialized. Call setApp() first.');
    }
    return snackbarInstance.open(assign(options, { message })) as BNoticeComponent;
  }

  warn(messages: string | string[], options: SnackbarOptions = {}): void {
    if (!snackbarInstance) {
      throw new Error('SnackbarService: app not initialized. Call setApp() first.');
    }
    if (!(messages instanceof Array)) {
      messages = [messages];
    }
    each(messages, (message: string) => {
      snackbarInstance!.open(assign(options, { message, type: 'is-warning' }));
    });
  }

  showBottomBannerMessage(message: string, options: SnackbarBannerOptions = {}): void {
    this.showBannerMessage(message, 'is-bottom', options);
  }

  showTopRightBannerMessage(message: string, options: SnackbarBannerOptions = {}): void {
    this.showBannerMessage(message, 'is-top-right', options);
  }

  private showBannerMessage(
    message: string,
    position: GlobalPositions,
    options: SnackbarBannerOptions
  ): void {
    const columnsClasses: string = options.isCentered ? 'columns is-vcentered' : 'columns';
    const iconHtml: string = options.icon ? `<i class="cls ${options.icon} m-r-10"></i>` : '';
    const messageContainerClass: string = options.icon ? 'is-flex is-align-items-center' : '';
    const html: string = `
        <div class="${columnsClasses}">
          <div class="column">
            <div class="${messageContainerClass}">
              ${iconHtml} <p>${message}</p>
            </div>
          </div>
          <div class="snackbar-custom-action column is-narrow is-clickable m-l-5 m-r--10 m-t-2">
            <i class="cls cls-remove has-text-primary"></i>
          </div>
        </div>
      `;

    const timeInSeconds: number = options.duration || 5000;
    const timeoutDurationInSeconds: number = timeInSeconds - 1000;
    const noticeComponent: BNoticeComponent = this.show(html, {
      type: 'is-success',
      queue: false,
      position,
      duration: timeInSeconds,
      container: '#app-container',
      actionText: null,
      ...options
    });

    const clickHandler: () => void = () => {
      removeListener();
      noticeComponent.close();
    };

    const removeListener: () => void = () => {
      document.querySelector('.snackbar-custom-action')?.removeEventListener('click', clickHandler);
    };

    if (!options.indefinite) {
      setTimeout(() => {
        removeListener();
      }, timeoutDurationInSeconds);
    }

    document.querySelector('.snackbar-custom-action')?.addEventListener('click', clickHandler);
  }
}

export default new SnackbarService();
